import { useEffect, useState, useSyncExternalStore } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plane, Loader2, X, ChevronDown, ChevronUp, ArrowRight, Plus, Briefcase, AlertTriangle, Clock, Share2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { FlightDetailsPanel, _normalizeIso } from "./FlightDetailsPanel";
import { AddTripDialog } from "./AddTripDialog";

/**
 * Dashboard-kort som visar BEVAKADE RESOR (multi-leg journeys).
 *
 * Varje resa expanderar till en kedja av legs med connection-risk-badge
 * mellan varje par. Klick på ett leg expanderar dess FlightDetailsPanel.
 *
 * Pollar /api/trips var 30s (live data uppdateras på backend).
 */

// ─── Extern poll-store ──────────────────────────────────────────────────
let _trips = [];
let _loaded = false;
const _listeners = new Set();
let _timer = null;
let _subs = 0;

async function _fetch() {
  try {
    const { data } = await api.get("/trips");
    _trips = data?.trips || [];
  } catch { /* tyst */ }
  _loaded = true;
  _listeners.forEach((cb) => cb());
}
function _ensure() {
  if (_timer != null) return;
  void _fetch();
  _timer = setInterval(() => { void _fetch(); }, 30_000);
}
function _stop() {
  if (_timer != null) { clearInterval(_timer); _timer = null; }
}
function subscribeTrips(cb) {
  _listeners.add(cb);
  _subs += 1;
  _ensure();
  return () => {
    _listeners.delete(cb);
    _subs -= 1;
    if (_subs === 0) _stop();
  };
}
const _snap = () => _trips;
const _serverSnap = () => [];
export function refreshTrips() { return _fetch(); }

// ─── Hjälpare ───────────────────────────────────────────────────────────
function _formatTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(_normalizeIso(iso)).toLocaleTimeString("sv-SE", {
      hour: "2-digit", minute: "2-digit", timeZone: "Europe/Stockholm",
    });
  } catch { return "—"; }
}
function _formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(_normalizeIso(iso)).toLocaleDateString("sv-SE", {
      weekday: "short", day: "numeric", month: "short", timeZone: "Europe/Stockholm",
    });
  } catch { return ""; }
}

const RISK_TONE = {
  ok:         { color: "var(--status-ok, #4caf50)", icon: "✓",  bg: "rgba(76,175,80,0.12)" },
  tight:      { color: "#d4a017",                    icon: "⏱", bg: "rgba(212,160,23,0.14)" },
  threatened: { color: "#e07c1f",                    icon: "⚠", bg: "rgba(224,124,31,0.16)" },
  missed:     { color: "#c0392b",                    icon: "🚨", bg: "rgba(192,57,43,0.16)" },
  unknown:    { color: "var(--text-tertiary)",       icon: "—",  bg: "rgba(255,255,255,0.04)" },
};

function ConnectionBadge({ conn }) {
  if (!conn) return null;
  const tone = RISK_TONE[conn.status] || RISK_TONE.unknown;
  return (
    <div
      data-testid={`connection-${conn.from_flight_id}-${conn.to_flight_id}`}
      className="flex items-center gap-2 px-3 py-2 rounded-md mx-3 my-1 text-xs"
      style={{ background: tone.bg, color: tone.color }}
    >
      <span className="text-base leading-none">{tone.icon}</span>
      <div className="flex-1 min-w-0">
        <div className="font-medium">{conn.label}</div>
        {conn.mct_source === "adb" && (
          <div className="opacity-70 text-[10px] mt-0.5">MCT från AeroDataBox</div>
        )}
      </div>
    </div>
  );
}

// ─── En leg-rad ─────────────────────────────────────────────────────────
function LegRow({ leg, expanded, onToggle, legNum, isFinal }) {
  const s = leg.last_status || {};
  const dest = s.destination_name || leg.destination_name || s.destination || "?";
  const orig = s.origin_name || s.origin || (leg.kind === "departure" ? "ARN" : "?");
  const schedDep = s.departure_scheduled || s.scheduled_time;
  const schedArr = s.arrival_scheduled || s.scheduled_time;
  const status = s.aero_status_text || s.remark_swedish || "—";

  return (
    <div data-testid={`trip-leg-${leg.flight_id}`} className="border-l-2 border-[color:var(--bg-elevated)]">
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left p-3 hover:bg-[color:var(--bg-elevated)] transition flex items-start gap-3"
      >
        <span className="text-[10px] font-bold uppercase text-[color:var(--text-tertiary)] mt-1 tabular-nums">
          {legNum}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-sm">
            <span className="font-semibold tabular-nums">{leg.flight_id}</span>
            <span className="text-[color:var(--text-tertiary)] truncate">
              {orig} <ArrowRight size={11} className="inline -mt-0.5" /> {dest}
            </span>
          </div>
          <div className="text-xs text-[color:var(--text-tertiary)] mt-0.5 flex items-center gap-2 flex-wrap">
            <span className="tabular-nums">{_formatTime(schedDep)} → {_formatTime(schedArr)}</span>
            <span>·</span>
            <span>{_formatDate(schedDep)}</span>
            {status && status !== "—" && (
              <>
                <span>·</span>
                <span>{status}</span>
              </>
            )}
            {isFinal && (
              <span className="ml-1 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider bg-[color:var(--accent-soft,rgba(99,102,241,0.15))] text-[color:var(--accent)]">
                Slutdestination
              </span>
            )}
          </div>
        </div>
        {expanded
          ? <ChevronUp size={16} className="mt-1 text-[color:var(--text-tertiary)]" />
          : <ChevronDown size={16} className="mt-1 text-[color:var(--text-tertiary)]" />}
      </button>
      {expanded && (
        <div className="px-2 pb-3">
          <FlightDetailsPanel watch={leg} />
        </div>
      )}
    </div>
  );
}

// ─── En resa ─────────────────────────────────────────────────────────────
function TripCard({ trip, onRemove }) {
  const { user } = useAuth();
  const isOwner = user?.id === trip.user_id && !user?.is_child;
  const [sharing, setSharing] = useState(false);
  const shareWithFamily = async () => {
    setSharing(true);
    try {
      const { data } = await api.post(`/trips/${trip.id}/share-with-family`);
      toast.success(data.newly_added?.length
        ? `Resan delas nu med ${data.newly_added.join(", ")}`
        : "Resan delas redan med hela familjen");
      await refreshTrips();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunde inte dela resan");
    } finally {
      setSharing(false);
    }
  };
  const [open, setOpen] = useState(true);
  const [expandedLegId, setExpandedLegId] = useState(null);
  const legs = trip.legs || [];
  const connections = trip.connections || [];

  // Värsta connection-status för header-badge
  const worstStatus = (() => {
    const order = ["missed", "threatened", "tight", "ok", "unknown"];
    for (const s of order) {
      if (connections.some((c) => c.status === s)) return s;
    }
    return "unknown";
  })();
  const worstTone = RISK_TONE[worstStatus] || RISK_TONE.unknown;

  // Totala resans tid: första legs avgång → sista legs ankomst.
  // Visas BARA för sammanhängande resor — ligger det >1 dygn mellan två legs
  // (utresa + hemresa) är "total restid" meningslös.
  const contiguous = legs.every((l, i) => {
    if (i === 0) return true;
    try {
      const prev = new Date(legs[i - 1]?.date).getTime();
      const cur = new Date(l?.date).getTime();
      return Number.isFinite(prev) && Number.isFinite(cur) && cur - prev <= 86400000;
    } catch {
      return false;
    }
  });
  const firstDep = (legs[0]?.last_status || {}).scheduled_time
    || (legs[0]?.last_status || {}).departure_scheduled;
  const lastArr = (legs[legs.length - 1]?.last_status || {}).arrival_scheduled
    || (legs[legs.length - 1]?.last_status || {}).scheduled_time;

  let totalDur = "";
  try {
    if (contiguous && firstDep && lastArr) {
      const d = new Date(_normalizeIso(firstDep)).getTime();
      const a = new Date(_normalizeIso(lastArr)).getTime();
      if (a > d) {
        const min = Math.round((a - d) / 60000);
        totalDur = min < 60 ? `${min} min` : `${Math.floor(min/60)}h ${min%60}min`;
      }
    }
  } catch { /* tyst */ }

  // Bygg ordnade element: leg, connection, leg, connection, ...
  const items = [];
  for (let i = 0; i < legs.length; i++) {
    items.push({ kind: "leg", leg: legs[i], idx: i });
    if (i < legs.length - 1) {
      const conn = connections.find(
        (c) => c.from_leg_id === legs[i].id && c.to_leg_id === legs[i + 1]?.id,
      );
      if (conn) items.push({ kind: "conn", conn });
    }
  }

  return (
    <li className="card-soft overflow-hidden" data-testid={`trip-card-${trip.id}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left p-3 flex items-start gap-3 hover:bg-[color:var(--bg-elevated)] transition"
        data-testid={`trip-toggle-${trip.id}`}
      >
        <Briefcase size={18} className="mt-0.5 text-[color:var(--accent-color,#6366f1)]" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm">{trip.label}</span>
            <span className="text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider bg-[color:var(--bg-elevated)] text-[color:var(--text-tertiary)]">
              {legs.length} {legs.length === 1 ? "flyg" : "flyg"}
            </span>
            {worstStatus !== "unknown" && worstStatus !== "ok" && (
              <span
                className="text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider font-semibold"
                style={{ background: worstTone.bg, color: worstTone.color }}
                data-testid={`trip-risk-${trip.id}`}
              >
                {worstTone.icon} {worstStatus === "threatened" ? "Anslutning hotad"
                  : worstStatus === "missed" ? "Anslutning missad"
                  : "Knapp anslutning"}
              </span>
            )}
          </div>
          <div className="text-xs text-[color:var(--text-tertiary)] mt-0.5">
            {legs[0]?.flight_id || "—"}
            {legs.length > 1 && (
              <> · {legs.slice(1).map((l) => l.flight_id).join(" · ")}</>
            )}
            {totalDur && (
              <> · <Clock size={10} className="inline -mt-0.5" /> {totalDur} totalt</>
            )}
            {trip.interline_baggage === false && (
              <> · <Briefcase size={10} className="inline -mt-0.5" /> Bagage hämtas vid byte</>
            )}
          </div>
        </div>
        {open
          ? <ChevronUp size={16} className="mt-1 text-[color:var(--text-tertiary)]" />
          : <ChevronDown size={16} className="mt-1 text-[color:var(--text-tertiary)]" />}
      </button>

      {isOwner && (
        <div className="flex items-center justify-end gap-3 px-3 pb-2">
          <button type="button" onClick={shareWithFamily} disabled={sharing}
            className="flex items-center gap-1.5 text-xs text-[color:var(--accent)] disabled:opacity-50"
            data-testid={`trip-share-${trip.id}`}>
            {sharing ? <Loader2 size={14} className="animate-spin" /> : <Share2 size={14} />}
            Dela med familjen
          </button>
          <button type="button" onClick={() => onRemove(trip)}
            className="p-1 hover:bg-[color:var(--bg-elevated)] rounded"
            data-testid={`trip-remove-${trip.id}`} aria-label="Ta bort resa">
            <X size={14} className="text-[color:var(--text-tertiary)]" />
          </button>
        </div>
      )}

      {open && (
        <div className="pb-2">
          {items.map((item, i) =>
            item.kind === "leg" ? (
              <LegRow
                key={`leg-${item.leg.id || i}`}
                leg={item.leg}
                legNum={item.idx + 1}
                isFinal={item.leg.is_final_leg}
                expanded={expandedLegId === item.leg.id}
                onToggle={() => setExpandedLegId(expandedLegId === item.leg.id ? null : item.leg.id)}
              />
            ) : (
              <ConnectionBadge key={`conn-${i}`} conn={item.conn} />
            )
          )}
        </div>
      )}
    </li>
  );
}

// ─── Huvudkomponent ─────────────────────────────────────────────────────
export function TripsCard() {
  const trips = useSyncExternalStore(subscribeTrips, _snap, _serverSnap);
  const loading = !_loaded;
  const [showAdd, setShowAdd] = useState(false);

  const removeTrip = async (trip) => {
    const ok = window.confirm(
      `Ta bort resan "${trip.label}"?\n\n` +
      `(Flygen behålls som fristående bevakningar. Använd ångra-knappen i settings om du vill rensa dem helt.)`
    );
    if (!ok) return;
    try {
      await api.delete(`/trips/${trip.id}`);
      toast.success("Resa borttagen");
      await _fetch();
      // Refresh flights så de fristående watches visas i FlightsCard
      try {
        const { refreshFlights } = await import("./FlightsCard");
        await refreshFlights();
      } catch { /* tyst */ }
    } catch {
      toast.error("Kunde inte ta bort resan");
    }
  };

  if (loading && trips.length === 0) return null;
  // Visa knappen även när inga resor finns? Ja — så användaren kan upptäcka feature.

  return (
    <section data-testid="trips-card" className="px-4">
      <h2 className="text-xs font-bold uppercase tracking-wider text-[color:var(--text-tertiary)] mb-2 flex items-center justify-between gap-1.5">
        <span className="flex items-center gap-1.5">
          <Plane size={14} />
          Resor under bevakning
        </span>
        <button
          type="button"
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-1 px-2 py-1 rounded text-[10px] hover:bg-[color:var(--bg-elevated)] transition normal-case font-medium"
          data-testid="trips-add-button"
        >
          <Plus size={12} />
          Lägg till resa
        </button>
      </h2>
      {trips.length > 0 && (
        <ul className="space-y-2">
          {trips.map((t) => (
            <TripCard key={t.id} trip={t} onRemove={removeTrip} />
          ))}
        </ul>
      )}
      <AddTripDialog
        open={showAdd}
        onClose={() => setShowAdd(false)}
        onCreated={async () => {
          setShowAdd(false);
          await _fetch();
          try {
            const { refreshFlights } = await import("./FlightsCard");
            await refreshFlights();
          } catch { /* tyst */ }
        }}
      />
    </section>
  );
}
